import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, isAfter, isSameDay } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { Icon, Label } from 'semantic-ui-react';
import { Table } from '~/components';
import { useUserContext } from '~/hooks';
import { useQueryParamState } from '~/hooks/useQueryParamState';
import { useProjectList } from '~/requests/projects/useProjectList';
import { DASHBOARD_QUERY_PARAM_STATE } from './constants';
import { DefaultSortOrders, filterProjects } from '~/utils/projectListReducer';
import { ProjectFiltersType } from '~/types/project/ProjectFiltersType';
import { orderBy } from 'lodash';
import { formatNumber } from '~/utils';
import ProjectStatusBadge from './components/ProjectStatusBadge';
import { ProjectWorkflow } from '~/constants/statuses';

interface DealRowData {
  id: string;
  type: 'project' | 'portfolio';
  name: string;
  link: string;
  developer: string;
  /** Hashed company id when the row resolves to a single developer company (admin list API). */
  developerCompanyId?: string;
  investor: string;
  /** Hashed company id when the row resolves to a single investor company (admin list API). */
  investorCompanyId?: string;
  pvMwdc: number;
  bessMw: number;
  bidType: string;
  revenueType: string;
  state: string;
  status: string;
  bidDeadline?: string;
  isArchived: boolean;
  lastUpdate: Date | null;
  projects: any[];
}

const PRICING_TYPE_DISPLAY: Record<string, string> = {
  ACQUISITION: 'Acq',
  OFFTAKE: 'PPA',
  ITC: 'ITC',
  DEV_FEE: 'Dev Fee',
};

const DEPLOYMENT_TYPE_DISPLAY: Record<string, string> = {
  CI: 'C&I',
  COMMUNITY_SOLAR: 'Community',
  UTILITY: 'Utility',
};

const getProjectDealLink = (project: any) => {
  return project.status === 'MARKET_QUOTE'
    ? `/pricing-estimate/${project.id}/worksheet`
    : `/project/${project.id}/review`;
};

const getDisplayValue = (values: Array<string | undefined>, displayMap: Record<string, string>) => {
  const unique = Array.from(new Set(values.filter(Boolean) as string[]));
  if (unique.length === 0) return '-';
  if (unique.length > 1) return 'Mixed';
  return displayMap[unique[0]] ?? unique[0];
};

const toStateCode = (location?: string) => {
  if (!location) return '';
  const parts = location.split(', ');
  return parts.length > 1 ? parts[parts.length - 1] : location;
};

const getDealState = (projects: any[]) => {
  const unique = Array.from(new Set(projects.map((project) => toStateCode(project.location)).filter(Boolean)));
  if (unique.length === 0) return '-';
  return unique.length > 1 ? 'Multiple' : unique[0];
};

const getDealStatus = (projects: any[]) => {
  const unique = Array.from(new Set(projects.map((project) => project.statusDisplay).filter(Boolean)));
  if (unique.length === 0) return '-';
  return unique.length > 1 ? 'Mixed' : unique[0];
};

const DILIGENCE_OR_BEYOND = new Set([
  ProjectWorkflow.IN_DILIGENCE,
  ProjectWorkflow.EXECUTED,
  ProjectWorkflow.COMPLETED,
]);

const isInDiligenceOrBeyond = (project: any) =>
  DILIGENCE_OR_BEYOND.has(project.statusDisplay);

// Show the bid deadline only while a deal is still in (or before) bidding. Once a
// deal reaches diligence or later, or has no deadline set, render a placeholder.
// A deadline that has already passed is shown in red.
const renderBidDeadline = (bidDeadline?: string, status?: string) => {
  if (!bidDeadline || (status && DILIGENCE_OR_BEYOND.has(status))) return '-';
  const zonedDeadline = utcToZonedTime(bidDeadline, 'UTC');
  const nowUtc = utcToZonedTime(new Date(), 'UTC');
  const formatted = format(zonedDeadline, 'MM/dd/yyyy');
  // The deadline is "passed" only once the current date is strictly after it;
  // a deadline falling on the current date is not passed.
  const hasPassed = !isSameDay(nowUtc, zonedDeadline) && isAfter(nowUtc, zonedDeadline);
  return hasPassed ? <span style={{ color: 'var(--color-red)' }}>{formatted}</span> : formatted;
};

const getDealInvestor = (projects: any[]) => {
  const qualifying = projects.filter(isInDiligenceOrBeyond);
  if (qualifying.length === 0) return '-';
  const unique = Array.from(new Set(qualifying.map((p) => p.investorName).filter(Boolean)));
  if (unique.length === 0) return '-';
  return unique.length > 1 ? 'Mixed' : unique[0];
};

const getDealInvestorCompanyId = (projects: any[]) => {
  const qualifying = projects.filter(isInDiligenceOrBeyond);
  if (qualifying.length === 0) return undefined;
  const nameSet = new Set(qualifying.map((p) => p.investorName).filter(Boolean));
  if (nameSet.size === 0 || nameSet.size > 1) return undefined;
  const companyIds = Array.from(
    new Set(qualifying.map((p) => p.investorCompanyId).filter(Boolean))
  ) as string[];
  if (companyIds.length !== 1) return undefined;
  return companyIds[0];
};

const canUseStatusBadge = (status: string) => {
  return status !== 'Mixed' && status !== '-';
};

const sortDeals = (deals: DealRowData[], column: string, direction: 'asc' | 'desc') => {
  const iteratee = (deal: DealRowData) => {
    switch (column) {
      case 'name':
        return deal.name.toLowerCase();
      case 'projectCount':
        return deal.projects.length;
      case 'developer':
        return deal.developer.toLowerCase();
      case 'investor':
        return deal.investor.toLowerCase();
      case 'pvMwdc':
        return deal.pvMwdc;
      case 'bessMw':
        return deal.bessMw;
      case 'bidType':
        return deal.bidType.toLowerCase();
      case 'revenueType':
        return deal.revenueType.toLowerCase();
      case 'state':
        return deal.state.toLowerCase();
      case 'status':
        return deal.status.toLowerCase();
      case 'updatedAt':
      default:
        return deal.lastUpdate ? deal.lastUpdate.getTime() : 0;
    }
  };

  return orderBy(deals, [iteratee], [direction]);
};

interface ProjectTableProps {
  projectFilters: ProjectFiltersType;
}

export default function ProjectTable(props: ProjectTableProps) {
  const { loggedInUser } = useUserContext();
  const showDeveloperColumn = !loggedInUser.isInstaller || loggedInUser.isAdmin;
  const showInvestorColumn = !loggedInUser.isInvestor || loggedInUser.isAdmin;

  // Fetch and cache project list
  const { data: projects, status } = useProjectList({
    showArchived: true
  });

  // Get filter/sort data from the query param json state
  const [dashboardState, setDashboardState] = useQueryParamState(DASHBOARD_QUERY_PARAM_STATE);

  const { projectFilters } = props;

  const ownerIds = dashboardState?.ownerIds;
  const sortColumn = dashboardState?.sort ?? 'updatedAt';
  const sortDirection = dashboardState?.sortDirection ?? 'desc';

  const [expandedDeals, setExpandedDeals] = useState<Record<string, boolean>>({});

  const filteredProjects = useMemo(() => {
    return filterProjects(projects ?? [], projectFilters).filter((project: any) =>
      ownerIds?.length ? ownerIds.includes(project.ownerId) : true
    );
  }, [projects, projectFilters, ownerIds]);

  const deals = useMemo(() => {
    const dealMap = new Map<string, DealRowData>();

    filteredProjects.forEach((project: any) => {
      const isPortfolioProject = Boolean(project.portfolioId);
      const dealId = isPortfolioProject ? `portfolio:${project.portfolioId}` : `project:${project.id}`;

      if (!dealMap.has(dealId)) {
        const starterProjects = [project];
        dealMap.set(dealId, {
          id: dealId,
          type: isPortfolioProject ? 'portfolio' : 'project',
          name: isPortfolioProject ? (project.portfolioName || 'Untitled Portfolio') : (project.name || 'Untitled Project'),
          link: isPortfolioProject ? `/portfolio/${project.portfolioId}/review` : getProjectDealLink(project),
          developer: project.installerName || '-',
          developerCompanyId: project.installerCompanyId,
          investor: getDealInvestor(starterProjects),
          investorCompanyId: getDealInvestorCompanyId(starterProjects),
          pvMwdc: (project.size || 0) / 1000,
          bessMw: (project.storageSize || 0) / 1000,
          bidType: getDisplayValue(starterProjects.map((item) => item.pricingType), PRICING_TYPE_DISPLAY),
          revenueType: getDisplayValue(starterProjects.map((item) => item.deploymentType), DEPLOYMENT_TYPE_DISPLAY),
          state: getDealState(starterProjects),
          status: getDealStatus(starterProjects),
          bidDeadline: project.bidDeadline,
          isArchived: Boolean(project.isArchived || project.portfolioIsArchived),
          lastUpdate: project.updatedAt ?? null,
          projects: starterProjects,
        });
        return;
      }

      const existingDeal = dealMap.get(dealId)!;
      const updatedProjects = [...existingDeal.projects, project];
      const newestUpdatedAt = [existingDeal.lastUpdate, project.updatedAt]
        .filter(Boolean)
        .sort((a: Date, b: Date) => b.getTime() - a.getTime())[0] ?? null;

      const sameDeveloperName = existingDeal.developer === project.installerName;
      const sameDeveloperCompany =
        !existingDeal.developerCompanyId ||
        !project.installerCompanyId ||
        existingDeal.developerCompanyId === project.installerCompanyId;
      const mergedDeveloper =
        sameDeveloperName && sameDeveloperCompany
          ? existingDeal.developer
          : 'Mixed';
      const mergedDeveloperCompanyId =
        mergedDeveloper === 'Mixed'
          ? undefined
          : (existingDeal.developerCompanyId ?? project.installerCompanyId);

      dealMap.set(dealId, {
        ...existingDeal,
        developer: mergedDeveloper,
        developerCompanyId: mergedDeveloperCompanyId,
        investor: getDealInvestor(updatedProjects),
        investorCompanyId: getDealInvestorCompanyId(updatedProjects),
        pvMwdc: existingDeal.pvMwdc + ((project.size || 0) / 1000),
        bessMw: existingDeal.bessMw + ((project.storageSize || 0) / 1000),
        bidType: getDisplayValue(updatedProjects.map((item) => item.pricingType), PRICING_TYPE_DISPLAY),
        revenueType: getDisplayValue(updatedProjects.map((item) => item.deploymentType), DEPLOYMENT_TYPE_DISPLAY),
        state: getDealState(updatedProjects),
        status: getDealStatus(updatedProjects),
        bidDeadline: existingDeal.bidDeadline ?? project.bidDeadline,
        isArchived: existingDeal.isArchived || Boolean(project.isArchived || project.portfolioIsArchived),
        lastUpdate: newestUpdatedAt,
        projects: updatedProjects,
      });
    });

    return sortDeals(Array.from(dealMap.values()), sortColumn, sortDirection);
  }, [filteredProjects, sortColumn, sortDirection]);

  useEffect(() => {
    setExpandedDeals((prev) => {
      const nextState: Record<string, boolean> = {};
      deals.forEach((deal) => {
        nextState[deal.id] = prev[deal.id] ?? false;
      });
      return nextState;
    });
  }, [deals]);

  // Functions for getting/updating sort
  const getSort = (column: string) => {
    const sort = sortColumn === column ? sortDirection : undefined;
    if (sort === 'asc') return 'ascending';
    if (sort === 'desc') return 'descending';
    return sort;
  };

  const updateSort = (column: string) => {
    setDashboardState({
      ...dashboardState,
      sort: column,
      sortDirection: sortColumn === column ? (sortDirection === 'asc' ? 'desc' : 'asc') : (DefaultSortOrders[column] ?? 'asc')
    });
  };

  const isLoading = status === 'loading';

  const companyNameCell = (label: string, companyId?: string) => {
    if (!loggedInUser.isAdmin || !companyId || label === '-' || label === 'Mixed') {
      return label;
    }
    return <Link to={`/company-profile/${companyId}`}>{label}</Link>;
  };

  const toggleExpanded = (dealId: string) => {
    setExpandedDeals((prev) => ({
      ...prev,
      [dealId]: !prev[dealId]
    }));
  };

  return (
    <div
      style={{
        paddingBottom: '2rem',
        width: '100%',
        overflowX: 'auto',
      }}
    >
      <div className="project-meta-data">
        {
          isLoading ? 'Loading...' :
            'Showing ' + deals.length + ' deals'
        }
      </div>
      {isLoading && <Table.Loader />}
      <div>
        <Table sortable isAdmin={loggedInUser.isAdmin}>
          <Table.Header style={{ position: 'sticky', top: 0 }}>
            <Table.Row>

              <Table.HeaderCell
                sorted={getSort('name')}
                onClick={() => updateSort('name')}
                textAlign={'left'}
              >
                Deal name
              </Table.HeaderCell>
              {showDeveloperColumn && (
              <Table.HeaderCell
                sorted={getSort('developer')}
                onClick={() => updateSort('developer')}
                textAlign="left"
              >
                Developer
              </Table.HeaderCell>
              )}
              {showInvestorColumn && (
              <Table.HeaderCell
                sorted={getSort('investor')}
                onClick={() => updateSort('investor')}
                textAlign="left"
              >
                Investor
              </Table.HeaderCell>
              )}
              <Table.HeaderCell
                sorted={getSort('projectCount')}
                onClick={() => updateSort('projectCount')}
                textAlign="right"
              >
                Project count
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={getSort('pvMwdc')}
                onClick={() => updateSort('pvMwdc')}
                textAlign="right"
              >
                PV (MWdc)
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={getSort('bessMw')}
                onClick={() => updateSort('bessMw')}
                textAlign="right"
              >
                BESS (MW)
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={getSort('bidType')}
                onClick={() => updateSort('bidType')}
                textAlign="left"
              >
                Bid type
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={getSort('state')}
                onClick={() => updateSort('state')}
                textAlign="left"
              >
                State
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={getSort('status')}
                onClick={() => updateSort('status')}
                textAlign="left"
              >
                Status
              </Table.HeaderCell>
              <Table.HeaderCell textAlign="right">
                Bid deadline
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={getSort('updatedAt')}
                onClick={() => updateSort('updatedAt')}
                textAlign="right"
              >
                Last update
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {deals.map((deal) => {
              const canExpandProjects = deal.type === 'portfolio' && deal.projects.length > 1;
              return (
              <Fragment key={deal.id}>
                <Table.Row className={deal.isArchived ? 'archived' : ''}>
                  <Table.Cell singleLine={false}>
                    <div className="deal-name-cell">
                      {canExpandProjects && (
                        <button
                          type="button"
                          className="deal-expand-toggle"
                          onClick={() => toggleExpanded(deal.id)}
                          aria-label={expandedDeals[deal.id] ? 'Collapse projects' : 'Expand projects'}
                        >
                          <Icon name={expandedDeals[deal.id] ? 'caret down' : 'caret right'} />
                        </button>
                      )}
                      {!canExpandProjects && (
                        <span className="deal-expand-placeholder" aria-hidden="true" />
                      )}
                      <Link to={deal.link}>
                        {deal.name}
                      </Link>
                    </div>
                  </Table.Cell>
                  {showDeveloperColumn && (
                  <Table.Cell textAlign="left" singleLine={false}>
                    {companyNameCell(deal.developer, deal.developerCompanyId)}
                  </Table.Cell>
                  )}
                  {showInvestorColumn && (
                  <Table.Cell textAlign="left" singleLine={false}>
                    {companyNameCell(deal.investor, deal.investorCompanyId)}
                  </Table.Cell>
                  )}
                  <Table.Cell textAlign="right">{deal.projects.length}</Table.Cell>
                  <Table.Cell textAlign="right">{deal.pvMwdc > 0 ? formatNumber(deal.pvMwdc, 2) : '-'}</Table.Cell>
                  <Table.Cell textAlign="right">{deal.bessMw > 0 ? formatNumber(deal.bessMw, 2) : '-'}</Table.Cell>
                  <Table.Cell textAlign="left">{deal.bidType}</Table.Cell>
                  <Table.Cell textAlign="left">{deal.state}</Table.Cell>
                  <Table.Cell textAlign="left">
                    {canUseStatusBadge(deal.status) ? (
                      <ProjectStatusBadge
                        statusDisplay={deal.status}
                        isArchived={deal.isArchived}
                      />
                    ) : (
                      <Label color="grey">{deal.status}</Label>
                    )}
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    {renderBidDeadline(deal.bidDeadline, deal.status)}
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    {deal.lastUpdate ? format(new Date(deal.lastUpdate), 'MM/dd/yyyy') : '-'}
                  </Table.Cell>
                </Table.Row>
                {canExpandProjects && expandedDeals[deal.id] && (
                  <>
                    <Table.Row className="deal-projects-subheader">
                      <Table.Cell>Project name</Table.Cell>
                      {showDeveloperColumn && <Table.Cell></Table.Cell>}
                      {showInvestorColumn && <Table.Cell></Table.Cell>}
                      <Table.Cell></Table.Cell>
                      <Table.Cell textAlign="right">PV (MWdc)</Table.Cell>
                      <Table.Cell textAlign="right">BESS (MW)</Table.Cell>
                      <Table.Cell>Bid type</Table.Cell>
                      <Table.Cell>State</Table.Cell>
                      <Table.Cell>Status</Table.Cell>
                      <Table.Cell textAlign="right">Bid deadline</Table.Cell>
                      <Table.Cell textAlign="right">Last update</Table.Cell>
                    </Table.Row>
                    {deal.projects.map((project) => (
                      <Table.Row key={project.id} className="deal-projects-row">
                        <Table.Cell singleLine={false}>
                          <div className="deal-project-name-cell">
                            <Link to={getProjectDealLink(project)}>
                              {project.name || 'Untitled Project'}
                            </Link>
                          </div>
                        </Table.Cell>
                        {showDeveloperColumn && <Table.Cell></Table.Cell>}
                        {showInvestorColumn && <Table.Cell></Table.Cell>}
                        <Table.Cell></Table.Cell>
                        <Table.Cell textAlign="right">
                          {project.size > 0 ? formatNumber(project.size / 1000, 2) : '-'}
                        </Table.Cell>
                        <Table.Cell textAlign="right">
                          {project.storageSize > 0 ? formatNumber(project.storageSize / 1000, 2) : '-'}
                        </Table.Cell>
                        <Table.Cell>
                          {project.pricingType ? PRICING_TYPE_DISPLAY[project.pricingType] ?? project.pricingType : '-'}
                        </Table.Cell>
                        <Table.Cell>
                          {toStateCode(project.location) || '-'}
                        </Table.Cell>
                        <Table.Cell>
                          {project.statusDisplay ? (
                            <ProjectStatusBadge
                              statusDisplay={project.statusDisplay}
                              isArchived={Boolean(project.isArchived || project.portfolioIsArchived)}
                            />
                          ) : (
                            <Label color="grey">-</Label>
                          )}
                        </Table.Cell>
                        <Table.Cell textAlign="right">
                          {renderBidDeadline(project.bidDeadline, project.statusDisplay)}
                        </Table.Cell>
                        <Table.Cell textAlign="right">
                          {project.updatedAt ? format(new Date(project.updatedAt), 'MM/dd/yyyy') : '-'}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </>
                )}
              </Fragment>
            );
            })}
          </Table.Body>
        </Table>
      </div>

      {deals.length === 0 && <div className="empty-table">No deals to display with the current filter selection.</div>}
    </div>
  );
}